import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { GlassCard, Chip, Skeleton } from "@/v2/components/ui";
import { PulseDetail, pulseIcon } from "@/v2/components/PulseBar";
import { Activity, ChevronLeft, ChevronRight } from "lucide-react";

export default function Pulse() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["pulse-list", page],
    queryFn: async () => (await api.get(`/pulse/list?page=${page}&limit=15`)).data,
  });

  return (
    <div className="space-y-6" data-testid="pulse-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold flex items-center gap-2">
          <Activity className="text-[#91cab8]" /> نبض التعلّم
        </h1>
        <p className="text-[#bcc8cf] mt-2 text-sm">كل الإنجازات الإيجابية التي تحققت في مجتمع التعلّم — إنجاز واحد يلهم الجميع.</p>
      </div>

      {isLoading ? <Skeleton className="h-72 w-full" /> : (
        <div className="space-y-3">
          {(data?.rows || []).map((it, i) => {
            const I = pulseIcon(it.icon);
            return (
              <motion.button key={it.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }} onClick={() => setSelected(it)} data-testid={`pulse-row-${i}`}
                className="w-full text-start">
                <GlassCard hover className="p-4 flex items-center gap-4">
                  {it.actor_avatar
                    ? <img src={it.actor_avatar} alt="" className="w-11 h-11 rounded-full object-cover border border-[#bb9661]/30" />
                    : <span className="w-11 h-11 rounded-2xl bg-[#bb9661]/15 text-[#e3c799] flex items-center justify-center shrink-0"><I size={20} /></span>}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm sm:text-base">{it.text}</div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Chip tone="muted">{it.kind_label}</Chip>
                      {it.department && <Chip tone="emerald">{it.department}</Chip>}
                      {it.pinned && <Chip tone="gold">مثبّت</Chip>}
                      {it.cheers > 0 && <span className="text-[11px] text-[#e3c799]">👏 {it.cheers}</span>}
                    </div>
                  </div>
                  <span className="text-[11px] text-[#bcc8cf] shrink-0" dir="ltr">{(it.at || "").slice(0, 10)}</span>
                </GlassCard>
              </motion.button>
            );
          })}
          {(data?.rows || []).length === 0 && (
            <GlassCard className="p-10 text-center text-[#bcc8cf] text-sm">لا توجد إنجازات بعد — كن أول من يحقق إنجازًا اليوم!</GlassCard>
          )}
        </div>
      )}

      {(data?.pages || 1) > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
            className="v2-btn-ghost v2-btn-sm disabled:opacity-40"><ChevronRight size={15} /> السابق</button>
          <span className="text-sm text-[#bcc8cf]">صفحة {page} من {data.pages}</span>
          <button onClick={() => setPage((p) => Math.min(data.pages, p + 1))} disabled={page === data.pages}
            className="v2-btn-ghost v2-btn-sm disabled:opacity-40">التالي <ChevronLeft size={15} /></button>
        </div>
      )}

      {selected && (
        <PulseDetail item={selected} canCheer={data?.can_cheer} onClose={() => setSelected(null)}
          onCheered={() => { qc.invalidateQueries({ queryKey: ["pulse-list"] }); qc.invalidateQueries({ queryKey: ["pulse"] }); }} />
      )}
    </div>
  );
}
